'use client';
import { useEffect, useRef, useState, useCallback } from 'react';
import { getSupabaseToken } from './auth-fetch';
import { getImpersonationState } from './impersonation';

const SESSION_KEY = 'aureus_session_token';
const POLL_INTERVAL = 60000; // 60s au lieu de 10s — évite les 429

// La route /api/session EXIGE un Bearer token (sinon 401 → la session ne
// s'enregistre jamais et la protection « 1 appareil » reste inactive).
// On joint donc systématiquement le JWT Supabase à chaque appel.
async function sessionHeaders() {
  const token = await getSupabaseToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function getDeviceInfo() {
  try {
    const ua = navigator.userAgent || 'unknown';
    const platform = navigator.platform || 'unknown';
    const lang = navigator.language || 'unknown';
    const screen = typeof window !== 'undefined'
      ? `${window.screen?.width}x${window.screen?.height}`
      : 'unknown';
    return `${platform} | ${screen} | ${lang} | ${ua.substring(0, 100)}`;
  } catch {
    return 'unknown';
  }
}

export function useSessionGuard(user, onForceLogout) {
  const [sessionToken, setSessionToken] = useState(null);
  const [kicked, setKicked] = useState(false);
  const intervalRef = useRef(null);
  const registeredRef = useRef(false);
  const errorCountRef = useRef(0);

  const registerSession = useCallback(async () => {
    // Pendant une impersonation, ce `user` est le COMPTE CIBLE (admin.js le
    // fait pointer sur lui via verifyOtp) — pas un vrai login depuis un
    // nouvel appareil. S'enregistrer ici déclencherait le kick « 1 appareil »
    // de la route /api/session sur la VRAIE session du client, depuis
    // l'appareil de l'admin. Bug réel du 16/09/2026 : impersonner un client
    // déconnectait sa session active.
    if (!user?.id || registeredRef.current || getImpersonationState()) return;
    registeredRef.current = true;
    try {
      const res = await fetch('/api/session', {
        method: 'POST',
        headers: await sessionHeaders(),
        body: JSON.stringify({
          action: 'register',
          user_id: user.id,
          user_email: user.email,
          device_info: getDeviceInfo()
        })
      });
      // Si 429 ou erreur serveur — ignorer silencieusement, ne pas crasher
      if (!res.ok) { registeredRef.current = false; return; }
      const data = await res.json();
      if (data.session_token) {
        try { sessionStorage.setItem(SESSION_KEY, data.session_token); } catch {}
        setSessionToken(data.session_token);
      }
    } catch {
      registeredRef.current = false;
    }
  }, [user?.id, user?.email]);

  const validateSession = useCallback(async () => {
    let token;
    try { token = sessionStorage.getItem(SESSION_KEY); } catch {}
    if (!user?.id || !token) return;
    // Stop polling si trop d'erreurs consécutives
    if (errorCountRef.current > 3) return;
    try {
      const res = await fetch('/api/session', {
        method: 'POST',
        headers: await sessionHeaders(),
        body: JSON.stringify({ action: 'validate', user_id: user.id, session_token: token })
      });
      if (res.status === 429 || res.status >= 500) {
        errorCountRef.current++;
        return; // ignorer silencieusement
      }
      errorCountRef.current = 0;
      const data = await res.json();
      if (!data.valid) {
        setKicked(true);
        try { sessionStorage.removeItem(SESSION_KEY); } catch {}
        if (intervalRef.current) clearInterval(intervalRef.current);
      }
    } catch {
      errorCountRef.current++;
    }
  }, [user?.id]);

  const logoutSession = useCallback(async () => {
    let token;
    try { token = sessionStorage.getItem(SESSION_KEY); } catch {}
    if (!user?.id || !token) return;
    try {
      await fetch('/api/session', {
        method: 'POST',
        headers: await sessionHeaders(),
        body: JSON.stringify({ action: 'logout', user_id: user.id, session_token: token })
      });
    } catch {}
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) registerSession();
    return () => { registeredRef.current = false; };
  }, [user?.id, registerSession]);

  useEffect(() => {
    if (!sessionToken) return;
    // Premier check après 30s (pas 3s) pour laisser le temps à l'app de se stabiliser
    const firstCheck = setTimeout(validateSession, 30000);
    intervalRef.current = setInterval(validateSession, POLL_INTERVAL);
    return () => {
      clearTimeout(firstCheck);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [sessionToken, validateSession]);

  // ⚠️ FIX 18/04/2026 — beforeunload handler retiré :
  // Il envoyait un beacon "logout" à chaque fermeture d'onglet, invalidant
  // la session côté DB. Résultat : le lendemain la session Supabase était
  // considérée invalide → kick + signOut forcé → re-login mot de passe.
  // La politique "session unique" est conservée via registerSession qui
  // désactive les anciennes sessions à chaque nouveau login (action:'register').

  return { kicked, logoutSession, sessionToken };
}

export function SessionKickedModal({ onConfirm }) {
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 99999, backdropFilter: 'blur(8px)'
    }}>
      <div style={{
        background: '#1a1918', border: '1px solid #c6a34e33', borderRadius: 16,
        padding: '40px 36px', maxWidth: 440, textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
      }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2 style={{ color: '#c6a34e', fontSize: 20, fontWeight: 700, margin: '0 0 12px 0' }}>
          Session déconnectée
        </h2>
        <p style={{ color: '#a8a59e', fontSize: 14, lineHeight: 1.6, margin: '0 0 24px 0' }}>
          Votre compte a été connecté depuis un autre appareil.
          <br />Pour des raisons de sécurité, une seule session active est autorisée.
        </p>
        <button
          onClick={onConfirm}
          style={{
            background: 'linear-gradient(135deg, #c6a34e, #a8882e)', color: '#0c0b09',
            border: 'none', borderRadius: 8, padding: '12px 32px', fontSize: 14,
            fontWeight: 700, cursor: 'pointer', letterSpacing: '0.5px'
          }}
        >
          Se reconnecter
        </button>
      </div>
    </div>
  );
}
